import { normaliseRegistration } from './identity';
import type { FormatParser, ParsedPoint, ParsedTrack, TrackWarning } from './types';
import { ParseError } from './types';
import { metresToFeet } from './units';

/**
 * IGC, the FAI gliding logger format: fixed-width, one record per line.
 *
 * The B record is the fix, and its layout is positional rather than delimited:
 *
 *     B 110135 5206343N 00006198W A 00587 00558
 *     │ │      │        │         │ │     └ GNSS altitude, metres
 *     │ │      │        │         │ └────── pressure altitude, metres
 *     │ │      │        │         └───────── A = 3D fix, V = 2D, altitude suspect
 *     │ │      │        └─────────────────── longitude DDDMMmmm + E/W
 *     │ │      └──────────────────────────── latitude DDMMmmm + N/S
 *     │ └─────────────────────────────────── UTC time of day, HHMMSS
 *     └───────────────────────────────────── record type
 *
 * Two things make this more than a slice-and-parse:
 *
 *  - **There is no date in a B record.** It comes from the `HFDTE` header, and
 *    a flight that crosses midnight UTC has to have the day rolled forward when
 *    the time of day goes backwards, or the whole tail of the track lands
 *    twenty-three hours before its start.
 *  - **Both altitudes are present and they are not the same thing.** Pressure
 *    altitude is what a barograph trace is validated against; GNSS altitude is
 *    a geometric height. This parser prefers pressure altitude and says so,
 *    because that is the one a logbook and an altimeter agree about.
 */

/** `DDMMmmm` plus a hemisphere letter, as used for latitude and longitude. */
function parseDegrees(raw: string, degreeDigits: number): number {
  const degrees = Number(raw.slice(0, degreeDigits));
  const minutes = Number(raw.slice(degreeDigits, degreeDigits + 2));
  const thousandths = Number(raw.slice(degreeDigits + 2, degreeDigits + 5));
  const hemisphere = raw.slice(degreeDigits + 5, degreeDigits + 6).toUpperCase();

  if (!Number.isFinite(degrees) || !Number.isFinite(minutes) || !Number.isFinite(thousandths)) {
    return Number.NaN;
  }
  const value = degrees + (minutes + thousandths / 1000) / 60;
  return hemisphere === 'S' || hemisphere === 'W' ? -value : value;
}

/** A five-digit metre field, which may be signed for a below-datum pressure altitude. */
function parseAltitudeMetres(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '' || !/^-?\d+$/.test(trimmed)) {
    return Number.NaN;
  }
  return Number(trimmed);
}

/** `HFDTE150926` and `HFDTEDATE:150926,01` both mean 15 September 2026. */
function parseHeaderDate(line: string): { year: number; month: number; day: number } | undefined {
  const match = /^H[FO]DTE(?:DATE:)?\s*(\d{2})(\d{2})(\d{2})/i.exec(line.trim());
  if (!match) {
    return undefined;
  }
  const day = Number(match[1]);
  const month = Number(match[2]);
  const shortYear = Number(match[3]);
  if (day < 1 || day > 31 || month < 1 || month > 12) {
    return undefined;
  }
  /*
   * A two-digit year. IGC has no century, so the pivot is a choice: 70 and
   * above reads as 19xx, below it as 20xx. Powered GA logging started well
   * after 1970, and gliding records from the 1970s are not arriving as files.
   */
  return { year: shortYear >= 70 ? 1900 + shortYear : 2000 + shortYear, month, day };
}

function parseIgc(bytes: Uint8Array): ParsedTrack {
  const text = new TextDecoder().decode(bytes);
  const lines = text.split(/\r?\n/);

  let date: { year: number; month: number; day: number } | undefined;
  let registration: string | undefined;
  let gliderType: string | undefined;
  let pilot: string | undefined;

  const points: ParsedPoint[] = [];
  const warnings: TrackWarning[] = [];
  // Both altitude columns are kept until the file has been read, because which
  // one to use is a decision about the logger, not about an individual fix.
  const pressureMetres: number[] = [];
  const gnssMetres: number[] = [];
  let dayOffset = 0;
  let previousSecondsOfDay = -1;
  let twoDimensionalFixes = 0;
  let midnightRollovers = 0;

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (line === '') {
      continue;
    }
    const type = line[0]?.toUpperCase();

    if (type === 'H') {
      date ??= parseHeaderDate(line);
      // `HFGID` is the glider registration; `HFGTY` its type; `HFPLT` the pilot.
      const id = /^H[FO]GID(?:GLIDERID)?:?\s*(.*)$/i.exec(line);
      if (id?.[1] !== undefined && id[1].trim() !== '') {
        registration = normaliseRegistration(id[1].trim());
      }
      const type_ = /^H[FO]GTY(?:GLIDERTYPE)?:?\s*(.*)$/i.exec(line);
      if (type_?.[1] !== undefined && type_[1].trim() !== '') {
        gliderType = type_[1].trim();
      }
      const plt = /^H[FO]PLT(?:PILOTINCHARGE)?:?\s*(.*)$/i.exec(line);
      if (plt?.[1] !== undefined && plt[1].trim() !== '') {
        pilot = plt[1].trim();
      }
      continue;
    }

    if (type !== 'B' || line.length < 35) {
      continue;
    }

    const hours = Number(line.slice(1, 3));
    const minutes = Number(line.slice(3, 5));
    const seconds = Number(line.slice(5, 7));
    const secondsOfDay = hours * 3600 + minutes * 60 + seconds;

    if (!Number.isFinite(secondsOfDay)) {
      continue;
    }
    // Time of day going backwards means the flight crossed midnight UTC.
    if (previousSecondsOfDay !== -1 && secondsOfDay < previousSecondsOfDay) {
      dayOffset += 1;
      midnightRollovers += 1;
    }
    previousSecondsOfDay = secondsOfDay;

    const lat = parseDegrees(line.slice(7, 15), 2);
    const lon = parseDegrees(line.slice(15, 24), 3);
    const fixValidity = line[24]?.toUpperCase();
    const pressureM = parseAltitudeMetres(line.slice(25, 30));
    const gnssM = parseAltitudeMetres(line.slice(30, 35));

    if (fixValidity === 'V') {
      twoDimensionalFixes += 1;
    }

    pressureMetres.push(pressureM);
    gnssMetres.push(gnssM);

    points.push({
      tMs:
        date === undefined
          ? Number.NaN
          : Date.UTC(date.year, date.month - 1, date.day + dayOffset) + secondsOfDay * 1000,
      lat,
      lon,
      // Filled in below, once the file says which column to believe.
      altFt: Number.NaN,
      // IGC carries no speed or heading. Deriving them from position would be
      // inventing data the file does not contain.
      gsKt: Number.NaN,
      trackDeg: Number.NaN,
    });
  }

  if (points.length === 0) {
    throw new ParseError('This IGC file contains no B records (position fixes).');
  }

  /*
   * Which altitude column to use is one decision for the whole file.
   *
   * A logger with no barometric sensor writes `00000` in the pressure field for
   * every fix, and a per-fix "use pressure unless it is zero" rule would then
   * read a real sea-level fix as missing and flip between the two columns
   * mid-climb. Pressure altitude wins when the column carries any signal at
   * all, because that is the figure an altimeter and a logbook agree about;
   * otherwise the file is a GPS trace and says so.
   */
  const pressureHasSignal = pressureMetres.some((value) => Number.isFinite(value) && value !== 0);
  const chosen = pressureHasSignal ? pressureMetres : gnssMetres;
  const altitudeRef = pressureHasSignal ? 'baro' : 'gps';
  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];
    const metres = chosen[i];
    if (point !== undefined && metres !== undefined && Number.isFinite(metres)) {
      point.altFt = metresToFeet(metres);
    }
  }

  if (date === undefined) {
    warnings.push({
      kind: 'no-timestamps',
      message:
        'This IGC file has no HFDTE date header, so its fixes carry a time of day but no date.',
    });
  }
  if (twoDimensionalFixes > 0) {
    warnings.push({
      kind: 'implausible-altitude',
      message: `${String(twoDimensionalFixes)} fixes are marked as 2D (V), where the logger had no altitude solution.`,
    });
  }

  const notes = [
    `${String(points.length)} B records${
      midnightRollovers > 0 ? `, crossing midnight UTC ${String(midnightRollovers)} time(s)` : ''
    }`,
    pressureHasSignal
      ? 'Altitude is pressure altitude, as recorded by the logger'
      : 'No pressure altitude in this file, so altitude is the GNSS height',
  ];
  if (gliderType !== undefined) {
    notes.push(`Aircraft: ${gliderType}`);
  }
  if (pilot !== undefined) {
    notes.push(`Pilot: ${pilot}`);
  }

  return {
    source: 'igc',
    points,
    altitudeRef,
    ...(registration !== undefined && registration !== '' ? { registration } : {}),
    warnings,
    notes,
  };
}

export const igcParser: FormatParser = {
  format: 'igc',
  label: 'IGC',
  extensions: ['.igc'],
  // An IGC file starts with A followed by a three-character manufacturer code,
  // and every conforming file has at least one B record.
  sniff: (text) => /^A[A-Z0-9]{3}/m.test(text.slice(0, 2000)) && /^B\d{6}/m.test(text),
  parse: parseIgc,
};
